import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import theme from '../theme';

const monthNames = [
  'Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
  'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'
];
const dayNamesShort = ['L','M','M','G','V','S','D'];
const dayNamesFull = ['Dom','Lun','Mar','Mer','Gio','Ven','Sab'];

const legacyTimeSlotLabel = {
  mattina: '🌅 Mattina (8-13)',
  pomeriggio: '☀️ Pomeriggio (13-18)',
  sera: '🌙 Sera (18-22)',
  tutto_il_giorno: '🕐 Tutto il giorno',
  da_concordare: '💬 Da concordare',
};

function formatTimeSlot(value) {
  if (!value) return null;
  if (legacyTimeSlotLabel[value]) return legacyTimeSlotLabel[value];
  const match = value.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  if (match) return `🕐 Dalle ${match[1]} alle ${match[2]}`;
  return value;
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function formatYearMonth(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}

function EventCard({ ev }) {
  const isWorker = ev.role === 'worker';
  const isPending = ev.status === 'in attesa' || ev.status === 'controproposta';

  const borderColor = isPending ? theme.colors.textMuted : isWorker ? theme.colors.accent : theme.colors.primary;
  const badgeBg = isPending ? theme.colors.backgroundAlt : isWorker ? theme.colors.warningBg : theme.colors.primaryLight;
  const badgeColor = isPending ? theme.colors.textMuted : isWorker ? theme.colors.accent : theme.colors.primary;
  const badgeLabel = isPending
    ? (ev.status === 'controproposta' ? '💬 In negoziazione' : '⏳ In attesa')
    : isWorker ? 'Lavoratore' : 'Committente';

  const formattedTimeSlot = formatTimeSlot(ev.timeSlot);

  return (
    <div style={{
      background: isPending ? theme.colors.backgroundAlt : theme.colors.white,
      border: `1px solid ${theme.colors.border}`,
      borderLeft: `4px solid ${borderColor}`,
      borderRadius: theme.radius.md,
      padding: '10px 14px',
      marginBottom: 8,
      display: 'flex',
      gap: theme.spacing.sm,
      alignItems: 'flex-start',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: theme.font.size.md, color: theme.colors.textPrimary, marginBottom: 3 }}>
          {ev.title}
        </div>
        <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textSecondary, marginBottom: 4 }}>
          👤 {ev.counterpart}
        </div>
        {formattedTimeSlot && (
          <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
            {formattedTimeSlot}
          </div>
        )}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700,
        background: badgeBg,
        color: badgeColor,
        padding: '3px 8px', borderRadius: theme.radius.pill,
        whiteSpace: 'nowrap', alignSelf: 'flex-start',
      }}>
        {badgeLabel}
      </span>
    </div>
  );
}

function Calendario({ session, profile }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );

  const agendaScrollRef = useRef(null);
  const groupRefs = useRef({});
  const fixedHeaderRef = useRef(null);
  const isScrollingProgrammatically = useRef(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = toDateStr(today);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const ym = formatYearMonth(currentMonth);
    const monthEvents = events.filter(e => e.date.startsWith(ym));
    const sorted = [...monthEvents].sort((a, b) => a.date.localeCompare(b.date));
    if (sorted.find(e => e.date === todayStr)) {
      setSelectedDate(todayStr);
    } else if (sorted.length > 0) {
      setSelectedDate(sorted[0].date);
    } else {
      setSelectedDate(null);
    }
  }, [currentMonth]);

  useEffect(() => {
    if (!selectedDate) return;
    const el = groupRefs.current[selectedDate];
    const container = agendaScrollRef.current;
    if (!el || !container) return;

    isScrollingProgrammatically.current = true;

    const containerTop = container.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top;
    const scrollOffset = container.scrollTop + (elTop - containerTop) - 12;

    container.scrollTo({ top: scrollOffset, behavior: 'smooth' });

    setTimeout(() => {
      isScrollingProgrammatically.current = false;
    }, 600);
  }, [selectedDate]);

  const handleScroll = useCallback(() => {
    if (isScrollingProgrammatically.current) return;
    const container = agendaScrollRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top;
    const dates = Object.keys(groupRefs.current).sort();

    for (const date of dates) {
      const el = groupRefs.current[date];
      if (!el) continue;
      const elBottom = el.getBoundingClientRect().bottom;
      if (elBottom > containerTop) {
        const [y, m] = date.split('-').map(Number);
        const newMonth = new Date(y, m - 1, 1);
        if (formatYearMonth(newMonth) !== formatYearMonth(currentMonth)) {
          setCurrentMonth(newMonth);
        }
        setSelectedDate(date);
        break;
      }
    }
  }, [currentMonth]);

  async function fetchEvents() {
    setLoading(true);
    const myName = profile?.full_name || session?.user?.email;
    const myEmail = session?.user?.email;

    const { data: acceptedApps } = await supabase
      .from('applications')
      .select('id, selected_dates, job_id, status, jobs(title, author, time_slot)')
      .eq('applicant_email', myEmail)
      .eq('status', 'accettata');

    const { data: pendingApps } = await supabase
      .from('applications')
      .select('id, selected_dates, job_id, status, jobs(title, author, time_slot)')
      .eq('applicant_email', myEmail)
      .in('status', ['in attesa', 'controproposta']);

    const { data: myJobs } = await supabase
      .from('jobs')
      .select('id')
      .eq('author', myName);

    const myJobIds = (myJobs || []).map(j => j.id);
    let receivedApps = [];
    if (myJobIds.length > 0) {
      const { data } = await supabase
        .from('applications')
        .select('id, selected_dates, job_id, applicant_email, status, jobs(title, time_slot)')
        .in('job_id', myJobIds)
        .eq('status', 'accettata');
      receivedApps = data || [];
    }

    const emailsToResolve = [...new Set(receivedApps.map(a => a.applicant_email))];
    const nameMap = {};
    for (const email of emailsToResolve) {
      const { data } = await supabase.rpc('get_profile_by_email', { user_email: email });
      if (data && data.length > 0) nameMap[email] = data[0].full_name;
      else nameMap[email] = email.split('@')[0];
    }

    const allEvents = [];

    for (const app of (acceptedApps || [])) {
      const dates = Array.isArray(app.selected_dates) ? app.selected_dates : [];
      for (const date of dates) {
        allEvents.push({
          date,
          title: app.jobs?.title || 'Lavoro',
          counterpart: app.jobs?.author || '?',
          timeSlot: app.jobs?.time_slot || null,
          role: 'worker',
          status: 'accettata',
          appId: app.id,
        });
      }
    }

    for (const app of (pendingApps || [])) {
      const dates = Array.isArray(app.selected_dates) ? app.selected_dates : [];
      if (dates.length === 0) continue;
      for (const date of dates) {
        allEvents.push({
          date,
          title: app.jobs?.title || 'Lavoro',
          counterpart: app.jobs?.author || '?',
          timeSlot: app.jobs?.time_slot || null,
          role: 'worker',
          status: app.status,
          appId: app.id,
        });
      }
    }

    for (const app of receivedApps) {
      const dates = Array.isArray(app.selected_dates) ? app.selected_dates : [];
      const workerName = nameMap[app.applicant_email] || app.applicant_email;
      for (const date of dates) {
        allEvents.push({
          date,
          title: app.jobs?.title || 'Lavoro',
          counterpart: workerName,
          timeSlot: app.jobs?.time_slot || null,
          role: 'employer',
          status: 'accettata',
          appId: app.id,
        });
      }
    }

    allEvents.sort((a, b) => a.date.localeCompare(b.date));
    setEvents(allEvents);
    setLoading(false);
  }

  const eventsByDate = {};
  events.forEach(e => {
    if (!eventsByDate[e.date]) eventsByDate[e.date] = [];
    eventsByDate[e.date].push(e);
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayRaw = new Date(year, month, 1).getDay();
  const offset = firstDayRaw === 0 ? 6 : firstDayRaw - 1;
  const daysInPrev = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < offset; i++) {
    cells.push({ day: daysInPrev - offset + 1 + i, current: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true });
  }
  const remaining = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
  for (let i = 1; i <= remaining; i++) {
    cells.push({ day: i, current: false });
  }

  const formatDateLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const isToday = dateStr === todayStr;
    const base = dayNamesFull[d.getDay()] + ' ' + d.getDate() + ' ' + monthNames[d.getMonth()];
    return isToday ? 'Oggi, ' + base : base;
  };

  const agendaGroups = Object.entries(eventsByDate)
    .sort(([a], [b]) => a.localeCompare(b));

  const handleDayClick = (dateStr, dayEvents) => {
    if (dayEvents.length === 0) return;
    setSelectedDate(dateStr);
  };

  const confirmedCount = events.filter(e => e.status === 'accettata').length;
  const pendingCount = events.filter(e => e.status === 'in attesa' || e.status === 'controproposta').length;

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family, height: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* PARTE FISSA */}
      <div ref={fixedHeaderRef} style={{ flexShrink: 0, background: '#f5f5f5', zIndex: 100 }}>

        <div style={{ background: theme.colors.primary, padding: '20px 20px 16px', color: theme.colors.white }}>
          <h1 style={{ margin: 0, fontSize: theme.font.size.xxl }}>📅 Calendario</h1>
          <div style={{ margin: '4px 0 0', fontSize: theme.font.size.base, display: 'flex', gap: theme.spacing.md }}>
            {confirmedCount > 0 && (
              <span style={{ opacity: 0.9 }}>✅ {confirmedCount} {confirmedCount === 1 ? 'confermato' : 'confermati'}</span>
            )}
            {pendingCount > 0 && (
              <span style={{ opacity: 0.9 }}>⏳ {pendingCount} {pendingCount === 1 ? 'in attesa' : 'in attesa'}</span>
            )}
            {confirmedCount === 0 && pendingCount === 0 && (
              <span style={{ opacity: 0.85 }}>Nessun impegno in programma</span>
            )}
          </div>
        </div>

        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: theme.colors.primary, color: theme.colors.white,
            }}>
              <button
                onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
                style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>
                ‹
              </button>
              <span style={{ fontWeight: 700, fontSize: theme.font.size.lg }}>{monthNames[month]} {year}</span>
              <button
                onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
                style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>
                ›
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '8px 8px 0' }}>
              {dayNamesShort.map((d, i) => (
                <div key={i} style={{ textAlign: 'center', fontSize: theme.font.size.xs, color: theme.colors.textMuted, fontWeight: 600, padding: '2px 0' }}>
                  {d}
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '4px 8px 12px', gap: 2 }}>
              {cells.map((cell, i) => {
                if (!cell.current) {
                  return (
                    <div key={i} style={{ padding: '6px 0', textAlign: 'center', fontSize: theme.font.size.sm, color: '#ddd' }}>
                      {cell.day}
                    </div>
                  );
                }
                const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(cell.day).padStart(2,'0')}`;
                const dayEvents = eventsByDate[dateStr] || [];
                const isToday = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;
                const hasWorkerConfirmed = dayEvents.some(e => e.role === 'worker' && e.status === 'accettata');
                const hasEmployer = dayEvents.some(e => e.role === 'employer');
                const hasPending = dayEvents.some(e => e.status === 'in attesa' || e.status === 'controproposta');

                return (
                  <div
                    key={i}
                    onClick={() => handleDayClick(dateStr, dayEvents)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      padding: '5px 2px', borderRadius: theme.radius.sm,
                      cursor: dayEvents.length > 0 ? 'pointer' : 'default',
                      background: isSelected ? theme.colors.primaryLight : isToday ? '#f0f9f0' : 'transparent',
                      border: isSelected
                        ? `1.5px solid ${theme.colors.primary}`
                        : isToday
                        ? '1.5px solid #a5d6a7'
                        : '1.5px solid transparent',
                    }}>
                    <span style={{
                      fontSize: theme.font.size.sm,
                      fontWeight: isToday || isSelected ? 700 : 400,
                      color: isToday ? theme.colors.primary : theme.colors.textPrimary,
                      lineHeight: 1,
                      marginBottom: 3,
                    }}>
                      {cell.day}
                    </span>
                    <div style={{ display: 'flex', gap: 2, height: 5, alignItems: 'center' }}>
                      {hasWorkerConfirmed && <div style={{ width: 4, height: 4, borderRadius: theme.radius.circle, background: theme.colors.accent }} />}
                      {hasEmployer && <div style={{ width: 4, height: 4, borderRadius: theme.radius.circle, background: theme.colors.primary }} />}
                      {hasPending && <div style={{ width: 4, height: 4, borderRadius: theme.radius.circle, background: theme.colors.textMuted }} />}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: theme.spacing.lg, padding: '8px 16px 12px', borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: theme.font.size.xs, color: theme.colors.textSecondary }}>
                <span style={{ width: 8, height: 8, borderRadius: theme.radius.circle, background: theme.colors.accent, display: 'inline-block' }} />
                Lavoratore
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: theme.font.size.xs, color: theme.colors.textSecondary }}>
                <span style={{ width: 8, height: 8, borderRadius: theme.radius.circle, background: theme.colors.primary, display: 'inline-block' }} />
                Committente
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: theme.font.size.xs, color: theme.colors.textSecondary }}>
                <span style={{ width: 8, height: 8, borderRadius: theme.radius.circle, background: theme.colors.textMuted, display: 'inline-block' }} />
                In attesa
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* AGENDA SCORREVOLE */}
      <div
        ref={agendaScrollRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px 90px',
        }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: theme.colors.textMuted, padding: 20 }}>Caricamento...</p>
        ) : agendaGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.md }}>Nessun impegno in programma.</p>
            <p style={{ color: '#bbb', fontSize: theme.font.size.sm }}>
              Le date appariranno quando ti candidi o ricevi una candidatura.
            </p>
          </div>
        ) : (
          agendaGroups.map(([date, evs]) => (
            <div
              key={date}
              ref={el => groupRefs.current[date] = el}
              style={{ marginBottom: theme.spacing.lg }}>
              <div
                onClick={() => setSelectedDate(date)}
                style={{
                  fontSize: theme.font.size.xs,
                  fontWeight: 700,
                  color: date === selectedDate ? theme.colors.primary : theme.colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 8,
                  cursor: 'pointer',
                }}>
                {formatDateLabel(date)}
              </div>
              {evs.map((ev, idx) => (
                <EventCard key={idx} ev={ev} />
              ))}
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}

export default Calendario;